import axios, { AxiosInstance } from 'axios';
import KeycloakService from '../services/KeycloakService';
import { captureRecoverableState } from '../services/sessionRecovery';

export const initAxios = (
  OVERRIDE_URL?: string,
  requireAuth = true,
): AxiosInstance => {
  // 1. Create the base Axios instance WITHOUT the dynamic token
  const apiClient = axios.create({
    // eslint-disable-next-line
    baseURL: OVERRIDE_URL ? OVERRIDE_URL : process.env.REACT_APP_API_URL,
    timeout: 300000,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  // 2. If auth is required, attach the Interceptor
  if (requireAuth) {
    apiClient.interceptors.request.use(
      async (config) => {
        if (!KeycloakService.isLoggedIn()) {
          // Whatever page issued this request may have in-progress state
          // registered for recovery (see sessionRecovery.ts) -- snapshot it
          // before the logout that's about to wipe localStorage.
          captureRecoverableState();
          KeycloakService.doLogout({ preserveRecoveryDrafts: true });
          return Promise.reject(new Error('User is not logged in'));
        }

        try {
          // This will now throw an error if the refresh token is expired/dead
          await KeycloakService.updateToken();

          config.headers.Authorization = `Bearer ${KeycloakService.getToken()}`;
          return config;
        } catch (error) {
          // Log out ONLY when the identity provider explicitly rejected us.
          // Previously anything that wasn't a recognisable network error fell
          // through to doLogout(), so a 5xx from the token endpoint — or any
          // refresh error whose message didn't match the regex below — ended an
          // active user's session. Everything else is transient: reject this one
          // request and leave the session for the heartbeat to recover.
          if (KeycloakService.isSessionDeadError(error)) {
            console.error(
              'Interceptor caught an invalid/dead token session. Logging out.',
              error,
            );
            captureRecoverableState();
            KeycloakService.doLogout({ preserveRecoveryDrafts: true });
            return Promise.reject(error);
          }

          const isOffline =
            typeof window !== 'undefined' && window.navigator?.onLine === false;
          const isNetworkFailure =
            error instanceof TypeError ||
            (error instanceof Error &&
              /network|timeout|failed to fetch/i.test(error.message));
          console.warn(
            isOffline || isNetworkFailure
              ? 'Axios interceptor token refresh failed due to network status. Retaining session.'
              : 'Axios interceptor token refresh failed (transient). Retaining session.',
            error,
          );
          return Promise.reject(error);
        }
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error?.config as
          | (typeof error.config & { __biteAuthRetry?: boolean })
          | undefined;

        // A token can expire after the request interceptor's check or while a
        // laptop is waking. Refresh silently and replay the request once instead
        // of sending the 401 to React Query, whose legacy fallback redirects the
        // whole page through login.
        if (
          error?.response?.status !== 401 ||
          !originalRequest ||
          originalRequest.__biteAuthRetry
        ) {
          return Promise.reject(error);
        }

        originalRequest.__biteAuthRetry = true;

        try {
          await KeycloakService.updateToken(-1);
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${KeycloakService.getToken()}`;
          return apiClient.request(originalRequest);
        } catch (refreshError) {
          if (KeycloakService.isSessionDeadError(refreshError)) {
            console.error(
              '401 recovery found an invalid/dead session. Logging out.',
              refreshError,
            );
            captureRecoverableState();
            KeycloakService.doLogout({ preserveRecoveryDrafts: true });
          } else {
            console.warn(
              '401 recovery refresh failed transiently. Retaining session.',
              refreshError,
            );
          }
          return Promise.reject(refreshError);
        }
      },
    );
  }

  return apiClient;
};
